import { type MutableRefObject, useEffect, useRef } from 'react'

import { type Timestamp, timestampDate } from '@bufbuild/protobuf/wkt'
import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import {
  BiddingApprovalState,
  BiddingBidSendErrorCategory,
  BiddingBidSendStatus,
  BiddingIntradayOrderStatus,
  BiddingJepxReconcileOutcome,
} from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'
import {
  type BiddingIntradayOrderOrderMethod,
  BiddingIntradayOrderWithdrawState,
} from '@ebalance/proto/bffuser/v1/bidding_intraday_model_pb'
import { useTranslation } from 'react-i18next'

import { useErrorToast } from '~/hooks/useErrorToast'
import { appToaster } from '~/libs/toaster'
import { now as serverNow, serverTimeOffsetMs } from '~/models/clock'
import { isPartialMatchOnBoard } from '~/pages/IntradayBidding/models/intradayTableStatus'

/** 送信結果の通知に使う札の情報 (一覧項目の一部)。 */
export type SendNoticeOrder = {
  id: string
  sendStatus: BiddingBidSendStatus
  sendErrorCategory: BiddingBidSendErrorCategory
  approvalState: BiddingApprovalState
  bidStatus: BiddingIntradayOrderStatus
  withdrawState?: BiddingIntradayOrderWithdrawState
  jepxBidNo?: string
  /**
   * JEPX 入札番号を紐付けて板にあると backend が分かっている札か (AC-BIN-115)。 `jepxBidNo` は
   * 紐付けていない番号 (送信済の札に取り込みが見た番号、 #12633) も持つので、 紐付けはこの値で読む。
   */
  knownOnBoardByBidNo?: boolean
  timeCode: number
  direction: Side
  gcAt?: Timestamp
  updatedAt?: Timestamp
  /** 後から JEPX の板で見つけて直した時刻 (AC-BIN-115)。 */
  foundOnBoardAt?: Timestamp
  /** JEPX に 2 本出ている時の、 同じ札の eBalance の札の id (AC-BIN-115)。 */
  duplicateOfOrderId?: string
  /** 取り込んだ札に対して照合が決めた知らせの種別 (AC-BIN-057 / AC-BIN-115)。 */
  jepxReconcileOutcome?: BiddingJepxReconcileOutcome
  /**
   * 自動取消時刻に送信中から取消にした札を GC 後に板で見つけ、 取消のまま約定ゼロで残した札か
   * (AC-BIN-115)。 backend の導出で、 true なら見つけたことを知らせない (#12442 回答 2)。
   */
  foundOnBoardLeftUnchanged?: boolean
  /** 一部約定の札が板に残っているかの判定 (`isPartialMatchOnBoard`) に使う一覧の値。 */
  withdrawable?: boolean
  orderMethod?: BiddingIntradayOrderOrderMethod
  needsReview?: boolean
}

/** 観測中の札。 `retryNoticed` は再送待ち (SEND_FAILED) の通知を出し済みか。 */
type Watched = {
  retryNoticed: boolean
  /** 「取消を送りました」 (AC-BIN-057) を出し済みか。 */
  cancelSentNoticed?: boolean
}

const NOTICE_KEY = {
  retrying: 'page:biddingIntraday.sendResultToast.retrying',
  limitExceeded: 'page:biddingIntraday.sendResultToast.limitExceeded',
  approvalFlowChanged:
    'page:biddingIntraday.sendResultToast.approvalFlowChanged',
  jepxRejected: 'page:biddingIntraday.sendResultToast.jepxRejected',
  memberInfoMissing: 'page:biddingIntraday.sendResultToast.memberInfoMissing',
  workflowNotSent: 'page:biddingIntraday.sendResultToast.workflowNotSent',
  workflowJepxRejected:
    'page:biddingIntraday.sendResultToast.workflowJepxRejected',
  deadlineUnreached: 'page:biddingIntraday.sendResultToast.deadlineUnreached',
  cancelSent: 'page:biddingIntraday.sendResultToast.cancelSent',
  restoredToActive: 'page:biddingIntraday.sendResultToast.restoredToActive',
  settledByFills: 'page:biddingIntraday.sendResultToast.settledByFills',
  duplicateOnJepx: 'page:biddingIntraday.sendResultToast.duplicateOnJepx',
} as const

type NoticeKey = (typeof NOTICE_KEY)[keyof typeof NOTICE_KEY]

const IN_FLIGHT_STATUSES: ReadonlySet<BiddingBidSendStatus> = new Set([
  BiddingBidSendStatus.RESERVED,
  BiddingBidSendStatus.ENQUEUED,
  BiddingBidSendStatus.SENDING,
  BiddingBidSendStatus.SEND_FAILED,
])

/** 送る直前の再検証 (AC-BIN-089b / AC-BIN-118) で止めた札の原因分類。 */
const isRevalidationRefusal = (
  category: BiddingBidSendErrorCategory,
): boolean =>
  category === BiddingBidSendErrorCategory.INTERNAL_VALIDATION_VIOLATION ||
  category ===
    BiddingBidSendErrorCategory.INTERNAL_VALIDATION_VIOLATION_APPROVAL_MODE

/**
 * 送信 worker が下書きに戻した札の原因分類 → AC-BIN-057 の行。 再検証の違反に加え、 JEPX が
 * 内容を理由に受け付けなかった札 (AC-BIN-115) と JEPX 会員 ID 未登録で送れなかった札
 * (AC-IJM-009。 受渡契約コード未設定に分類される) も下書きに戻す。
 */
const RETURNED_TO_DRAFT_NOTICE_KEY: Partial<
  Record<BiddingBidSendErrorCategory, NoticeKey>
> = {
  [BiddingBidSendErrorCategory.INTERNAL_VALIDATION_VIOLATION]:
    NOTICE_KEY.limitExceeded,
  [BiddingBidSendErrorCategory.INTERNAL_VALIDATION_VIOLATION_APPROVAL_MODE]:
    NOTICE_KEY.approvalFlowChanged,
  [BiddingBidSendErrorCategory.VALIDATION_REJECTED]: NOTICE_KEY.jepxRejected,
  [BiddingBidSendErrorCategory.DELIVERY_CONTRACT_CODE_MISSING]:
    NOTICE_KEY.memberInfoMissing,
}

/**
 * 観測中の札が、 システムの送った板取消で取消処理中 (入札中・部分約定 + PENDING) か取消の終わりになったか
 * (AC-BIN-057 「取消を送りました」)。 システムの後着取消だけが JEPX 入札番号を紐付けてから
 * 取り消すので、 番号を紐付けていない取消 (利用者の送信失敗の札の取消、 送信済の札の別の画面からの
 * 板取消・自動取消) は含めない。 取消の成立が次の
 * 取得より前に終わった札は取消処理中を観測できないため、 取消 (約定ゼロ) の終わりでも拾う。
 * 取消処理中でない部分約定・全約定は送信の成功直後の約定と見分けられないので拾わない (時間前は即時に
 * 約定しうる)。 部分約定でも取消処理中なら取消を送っている札なので拾う。
 * この画面の WITHDRAW 応答を受けた札は利用者の取消なので除く。
 */
const cancelSentBySystem = (
  order: SendNoticeOrder,
  withdrawnHereIds: ReadonlySet<string> | undefined,
): boolean => {
  if (order.knownOnBoardByBidNo !== true) return false
  if (withdrawnHereIds?.has(order.id) === true) return false
  return (
    ((order.bidStatus === BiddingIntradayOrderStatus.PLACED ||
      order.bidStatus === BiddingIntradayOrderStatus.PARTIAL_MATCHED) &&
      order.withdrawState === BiddingIntradayOrderWithdrawState.PENDING) ||
    order.bidStatus === BiddingIntradayOrderStatus.CANCELLED
  )
}

/**
 * 同じ札が JEPX に 2 本出ていると分かって取り込んだ札 (`duplicateOfOrderId` が付いた札) の知らせ
 * (AC-BIN-057 / AC-BIN-115)。 種別は backend が取り込む時に決め (取消を積んだ札は取消を送りました、
 * GC 後に取り込んだ札は約定結果で確定しました、 それ以外は 2本出ています)、 画面は選ぶだけ。
 * 種別を持たない旧い取り込みの札は「2本出ています」。
 */
const duplicateNoticeKey = (order: SendNoticeOrder): NoticeKey => {
  switch (order.jepxReconcileOutcome) {
    case BiddingJepxReconcileOutcome.WITHDRAW_SENT:
      return NOTICE_KEY.cancelSent
    case BiddingJepxReconcileOutcome.TO_AWAITING_MATCH:
      return NOTICE_KEY.settledByFills
    default:
      return NOTICE_KEY.duplicateOnJepx
  }
}

/**
 * GC 前に失効した札か。 backend が GC 前に提出済の札を失効にするのは、 確定済・中断した
 * ワークフロー経由の札を送れなかった時だけ (それ以外の札は下書きに戻す。 AC-BIN-115 /
 * AC-BIN-134)。 一覧はワークフローの確定を持たないので、 失効にした時刻 (更新日時) で読む。
 */
const expiredBeforeGc = (order: SendNoticeOrder): boolean =>
  order.gcAt !== undefined &&
  order.updatedAt !== undefined &&
  timestampDate(order.updatedAt).getTime() < timestampDate(order.gcAt).getTime()

/**
 * 提出済の札が失効した時の行 (AC-BIN-057 / AC-BIN-115)。 GC 前の失効はワークフローの札を
 * 送れなかった理由で行を選び、 GC 以後の失効は「締切までに JEPX に届かず」 の行にする。
 * backend は締切後の内容の拒否も締切超過 (GATE_CLOSED) に分類するので、 締切後の拒否の行
 * (AC-BIN-099) とは分けられない。
 */
const expiredNoticeKey = (order: SendNoticeOrder): NoticeKey => {
  if (!expiredBeforeGc(order)) return NOTICE_KEY.deadlineUnreached
  const category = order.sendErrorCategory
  if (category === BiddingBidSendErrorCategory.VALIDATION_REJECTED) {
    return NOTICE_KEY.workflowJepxRejected
  }
  if (
    isRevalidationRefusal(category) ||
    category === BiddingBidSendErrorCategory.DELIVERY_CONTRACT_CODE_MISSING
  ) {
    return NOTICE_KEY.workflowNotSent
  }
  return NOTICE_KEY.deadlineUnreached
}

/** 前回観測した、 後から板で見つけて直した時刻 (ms) と 2 本の印。 */
type FoundObservation = {
  foundAtMs: number | undefined
  duplicateOfOrderId: string | undefined
}

/**
 * 後から JEPX の板で見つけて直した札の行 (AC-BIN-057 / AC-BIN-115)。 GC 前に見つけ、 システムの
 * 取消が取消処理中か失敗の札は「取消を送りました」。 一覧は取消の成立した時刻を持たず、 見つけた
 * 時に JEPX で取消済だった札と見分けられないので、 取消処理中を観測できないまま成立した札は
 * 「約定結果で確定しました」 に回す (送っていない取消を送ったと知らせないため)。 GC 前に見つけて
 * 入札中・全約定 (一部約定で残りが板にある札を含む) にした札は「入札中に戻しました」、 それ以外 (GC 後に見つけた札、 見つけた時に
 * 終わっていた札) は「約定結果で確定しました」。 ただし、 backend が「自動取消時刻に送信中から取消にした札を GC 後に
 * 見つけ、 取消のまま約定ゼロで残した」 と返した札 (`foundOnBoardLeftUnchanged`) は通知しない。
 * 前回の取得の結果やタブが裏にあったかに関わらず、 行の値だけで決める。
 * 下書きに戻した札・失効にした札が見つかって取消になった場合は知らせる (AC-BIN-057 / AC-BIN-115)。
 */
const foundNoticeKey = (
  order: SendNoticeOrder,
  foundAtMs: number,
): NoticeKey | undefined => {
  const foundBeforeGc =
    order.gcAt !== undefined && foundAtMs < timestampDate(order.gcAt).getTime()
  if (
    foundBeforeGc &&
    (order.withdrawState === BiddingIntradayOrderWithdrawState.PENDING ||
      order.withdrawState === BiddingIntradayOrderWithdrawState.FAILED)
  ) {
    return NOTICE_KEY.cancelSent
  }
  if (
    foundBeforeGc &&
    (order.bidStatus === BiddingIntradayOrderStatus.PLACED ||
      order.bidStatus === BiddingIntradayOrderStatus.ALL_MATCHED ||
      isPartialMatchOnBoard({
        ...order,
        withdrawable: order.withdrawable ?? false,
      }))
  ) {
    return NOTICE_KEY.restoredToActive
  }
  // GC 後に見つけても約定ゼロの「取消」のまま変わらない札は知らせない (#12442 の回答 2)。 backend の結論を見るだけ。
  if (order.foundOnBoardLeftUnchanged === true) return undefined
  return NOTICE_KEY.settledByFills
}

type SettledDecision =
  | { kind: 'notice'; key: NoticeKey }
  | { kind: 'silent' }
  /** JEPX に届いたかを確かめている札 (送信失敗の確定で、 約定軸がまだ終わっていない)。 */
  | { kind: 'wait' }

/**
 * 送信軸を出た札の結果の行。 下書きに戻した札は約定軸も下書き (締切で失効した下書きは
 * 送信の結果ではない)。 失効は提出済の札に限る。
 */
const decideSettled = (order: SendNoticeOrder): SettledDecision => {
  if (order.approvalState === BiddingApprovalState.DRAFT) {
    const key =
      order.bidStatus === BiddingIntradayOrderStatus.DRAFT
        ? RETURNED_TO_DRAFT_NOTICE_KEY[order.sendErrorCategory]
        : undefined
    return key === undefined ? { kind: 'silent' } : { kind: 'notice', key }
  }
  if (order.approvalState !== BiddingApprovalState.SUBMITTED) {
    return { kind: 'silent' }
  }
  if (order.bidStatus === BiddingIntradayOrderStatus.EXPIRED) {
    return { kind: 'notice', key: expiredNoticeKey(order) }
  }
  if (
    order.sendStatus === BiddingBidSendStatus.SEND_FAILED_FINAL &&
    (order.bidStatus === BiddingIntradayOrderStatus.PLACED ||
      order.bidStatus === BiddingIntradayOrderStatus.FAILED)
  ) {
    return { kind: 'wait' }
  }
  return { kind: 'silent' }
}

/**
 * 送信軸が空で観測した札の状態。 送信の結果で変わる軸 (送信軸・承認軸・約定軸・原因分類) だけを
 * 見る。 下書きの内容の編集では変わらないので、 編集を送信の結果と取り違えない。 同じ理由で
 * 下書きに戻った札が、 再提出の同期の送信でまた同じ理由で下書きに戻った時は区別できず、
 * 通知しない。
 */
const unsentStateKey = (order: SendNoticeOrder): string =>
  [
    order.sendStatus,
    order.approvalState,
    order.bidStatus,
    order.sendErrorCategory,
  ].join(':')

/** 送信軸が空で観測した札の前回の状態 (`unsentStateKey`) と、 その時の承認軸。 */
type UnsentObservation = {
  stateKey: string
  approvalState: BiddingApprovalState
}

/**
 * 開場時の自動送信と、 提出・承認の後の送信の結果を error トーストで知らせる (AC-BIN-057 /
 * AC-BIN-089a / AC-BIN-089b / AC-BIN-115 / AC-BIN-118 / AC-IJM-009)。 一覧の polling で
 * 結果の出ていない札 (予約中・送信待ち・送信中・再送待ち) を観測し、 結果を遷移として拾う。
 *
 * - 通信の不調で再送待ち (SEND_FAILED) になったのを初めて観測した時に、 送り直している旨を
 *   1 回出す。 システムは GC まで送り直すので、 失敗と読める文言にすると利用者が出し直して
 *   同じ札が 2 本出る
 * - 下書きに戻した札は、 下書きに残る原因分類で理由の行を選ぶ
 * - 提出済のまま失効した札は、 GC 前の失効かと原因分類で行を選ぶ。 送信失敗の確定の
 *   後、 失効か他の結果に決まるまでは観測を保つ
 *
 * - 下書きに戻した・失効にした札を後から JEPX の板で見つけて直した時 (一覧の
 *   `foundOnBoardAt` が前回の観測から変わった時。 削除していた札を戻した時は、 画面を開いた
 *   後に現れた札の `foundOnBoardAt` が開いた時刻以後の時) と、 同じ札が JEPX に 2 本出ている時
 *   (`duplicateOfOrderId` が付いた札が現れた時) に知らせる (AC-BIN-115)
 *
 * 画面を開く前に既に結果の出ていた札、 開いた時に既に再送待ちだった札は遷移を見ていないので
 * 知らせない。
 */
export function useSendFailureNotice(
  orders: readonly SendNoticeOrder[] | undefined,
  withdrawnHereIds?: MutableRefObject<Set<string>>,
): void {
  const { t } = useTranslation()
  const showErrorToast = useErrorToast()
  const watched = useRef<Map<string, Watched>>(new Map())
  // 一覧に現れた札 id (最初の一覧を取得するまで null)。 提出の RPC が送信まで同期で終え
  // る札は、 一覧が送信待ち・送信中を観測する前に結果 (確定失敗・下書きに戻した) で返る。
  // 画面を開いた後に初めて現れた札と、 送信軸が空 (下書き) で観測していた札が前回と違う
  // 結果になったら通知する。
  const seen = useRef<Set<string> | null>(null)
  // 送信軸が空で観測した札 → その時の状態。 下書きに戻した札も送信軸は空なので含める (直して
  // 再提出した札の同期の失敗も拾うため)。 前回と同じ状態の再取得では通知しない。
  const unsent = useRef<Map<string, UnsentObservation>>(new Map())
  // 観測した札 → 後から板で見つけて直した時刻と 2 本の印。 前回の観測から値が変わった札だけ
  // 知らせる。
  const found = useRef<Map<string, FoundObservation>>(new Map())
  // 最初の一覧を取得した時刻 (ms、 端末時計)。 削除していた札を取り込みが戻した時 (AC-BIN-115) は
  // 一覧に新しく現れるので、 見つけた時刻がこれ以後の札だけ知らせる。 最初の一覧はサーバー時計の
  // offset の確定 (session RPC の完了) より先に返りうるので、 端末時計で持ち、 比べる時点の offset を
  // 足してサーバー時計に直す。
  const openedAtBrowserMs = useRef<number | null>(null)

  useEffect(() => {
    if (orders === undefined) return
    const notifyWarning = (order: SendNoticeOrder, key: NoticeKey): void => {
      // warning type は useErrorToast の対象外のため appToaster を直接参照する。
      appToaster.create({
        type: 'warning',
        closable: true,
        duration: 8000,
        title: t(key, {
          koma: order.timeCode,
          side: t(
            order.direction === Side.BUY
              ? 'page:biddingSpot.side.buy'
              : 'page:biddingSpot.side.sell',
          ),
        }),
      })
    }
    const notify = (order: SendNoticeOrder, key: NoticeKey): void => {
      showErrorToast({
        title: t(key, {
          koma: order.timeCode,
          side: t(
            order.direction === Side.BUY
              ? 'page:biddingSpot.side.buy'
              : 'page:biddingSpot.side.sell',
          ),
        }),
        duration: 7000,
      })
    }

    const baselined = seen.current !== null
    const seenIds = seen.current ?? new Set<string>()
    seen.current = seenIds
    openedAtBrowserMs.current ??= serverNow().getTime() - serverTimeOffsetMs()
    const openedAt = openedAtBrowserMs.current + serverTimeOffsetMs()
    for (const order of orders) {
      const status = order.sendStatus
      const appearedAfterOpen = baselined && !seenIds.has(order.id)
      const previousUnsent = unsent.current.get(order.id)
      const stateKey = unsentStateKey(order)
      // 戻した下書きは原因分類が残るので、 利用者が承認申請→申請取消・差戻し→再編集で
      // 下書きに戻しても同じ「戻した下書き」の状態になる。 前回の観測が下書きでない札
      // (承認軸だけが動いた) の下書きへの遷移は利用者の操作で、 送信の結果ではない。
      const returnedByUser =
        status === BiddingBidSendStatus.UNSPECIFIED &&
        order.approvalState === BiddingApprovalState.DRAFT &&
        previousUnsent !== undefined &&
        previousUnsent.approvalState !== BiddingApprovalState.DRAFT
      const changedSinceUnsent =
        previousUnsent !== undefined &&
        previousUnsent.stateKey !== stateKey &&
        !returnedByUser
      seenIds.add(order.id)
      if (status === BiddingBidSendStatus.UNSPECIFIED) {
        unsent.current.set(order.id, {
          stateKey,
          approvalState: order.approvalState,
        })
      } else {
        unsent.current.delete(order.id)
      }
      const previous = watched.current.get(order.id)
      const previousFound = found.current.get(order.id)
      const foundAtMs =
        order.foundOnBoardAt === undefined
          ? undefined
          : timestampDate(order.foundOnBoardAt).getTime()
      found.current.set(order.id, {
        foundAtMs,
        duplicateOfOrderId: order.duplicateOfOrderId,
      })
      // 直した札は、 同じ周回で送信の結果 (取消を送った・送信失敗) に見えても重ねて通知しない。
      let foundNow = false
      const foundSinceSeen =
        previousFound !== undefined && foundAtMs !== previousFound.foundAtMs
      const restoredAfterOpen =
        appearedAfterOpen && foundAtMs !== undefined && foundAtMs >= openedAt
      if (foundAtMs !== undefined && (foundSinceSeen || restoredAfterOpen)) {
        foundNow = true
        const key = foundNoticeKey(order, foundAtMs)
        if (key === NOTICE_KEY.cancelSent && previous !== undefined) {
          previous.cancelSentNoticed = true
        }
        if (key !== undefined) notifyWarning(order, key)
      }
      if (
        order.duplicateOfOrderId !== undefined &&
        (appearedAfterOpen ||
          (previousFound !== undefined &&
            previousFound.duplicateOfOrderId === undefined))
      ) {
        notifyWarning(order, duplicateNoticeKey(order))
      }
      const observedBefore =
        previous !== undefined || appearedAfterOpen || changedSinceUnsent
      // 取消を送った札は JEPX に届いているので、 同じ周回で確定失敗に見えても送信失敗は通知しない。
      let cancelSentNow = false
      if (
        !foundNow &&
        previous !== undefined &&
        previous.cancelSentNoticed !== true &&
        cancelSentBySystem(order, withdrawnHereIds?.current)
      ) {
        previous.cancelSentNoticed = true
        cancelSentNow = true
        notifyWarning(order, NOTICE_KEY.cancelSent)
      }
      if (
        IN_FLIGHT_STATUSES.has(status) &&
        order.bidStatus !== BiddingIntradayOrderStatus.EXPIRED
      ) {
        // 開いた時に既に再送待ちだった札は、 再送待ちになった遷移を見ていない。
        const current = previous ?? {
          retryNoticed:
            !observedBefore && status === BiddingBidSendStatus.SEND_FAILED,
        }
        watched.current.set(order.id, current)
        if (
          status === BiddingBidSendStatus.SEND_FAILED &&
          order.sendErrorCategory === BiddingBidSendErrorCategory.TRANSIENT &&
          !current.retryNoticed
        ) {
          current.retryNoticed = true
          notify(order, NOTICE_KEY.retrying)
        }
        continue
      }
      if (!observedBefore) continue
      const decision = decideSettled(order)
      if (decision.kind === 'wait') {
        watched.current.set(order.id, previous ?? { retryNoticed: false })
        continue
      }
      watched.current.delete(order.id)
      if (decision.kind === 'notice' && !cancelSentNow && !foundNow) {
        notify(order, decision.key)
      }
    }
  }, [orders, withdrawnHereIds, showErrorToast, t])
}
